/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ShopperProducts } from '@salesforce/storefront-next-runtime/scapi';
import { Link } from '@/components/link';
import { ProductImage } from '@/components/product-image/product-image';
import type { DynamicImageDimensions } from '@/lib/images/dynamic-image';
import { SegmentPagination } from '@/components/segment-pagination';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { resolveImageUrl } from '@/lib/images/resolve-image-url';
import type { ImageContext } from '@/lib/images/resolve-image-url';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import type { AppConfig } from '@/types/config';

export interface TileImageCarouselProps {
    images: ShopperProducts.schemas['Image'][];
    productUrl: string;
    productName: string;
    widths?: DynamicImageDimensions;
    imgAspectRatio?: number;
    handleProductClick?: () => void;
    className?: string;
    /** Image context for Cloudinary URL transformation. When provided, image URLs are transformed. */
    context?: ImageContext;
}

/**
 * Product tile image carousel with infinite loop, CSS scroll-snap, drag-to-scroll,
 * hover-reveal arrows, and a thin segment pagination bar.
 *
 * Behaviour mirrors the Vans reference implementation (TileImageCarousel.ts / _b-tile_image_carousel.scss).
 * Only rendered when the product has more than one image for the selected colour variant.
 */
export function TileImageCarousel({
    images,
    productUrl,
    productName,
    widths,
    imgAspectRatio = 1,
    handleProductClick,
    className,
    context,
}: TileImageCarouselProps) {
    const { t } = useTranslation('common');
    const { t: tProduct } = useTranslation('product');
    const config = useConfig<AppConfig>();
    const trackRef = useRef<HTMLDivElement>(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isInited, setIsInited] = useState(false);

    // Refs for infinite-loop state (never trigger re-renders)
    const realSlideCount = useRef(images.length);
    const slideWidth = useRef(0);
    const isLoopJumping = useRef(false);

    // Refs for drag-to-scroll state
    const isDragging = useRef(false);
    const dragStartCoord = useRef(0);
    const dragStartScrollLeft = useRef(0);
    // dragActive: true between mousedown and the delayed cleanup; gates onMouseMove
    const dragActive = useRef(false);
    // 200ms snap/grabbing removal timer (mirrors grabbingRemoveTimeout)
    const grabCleanupTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    // isMouseNav drives scroll-snap-type disable (mirrors m-mousemove_navigation)
    const [isMouseNav, setIsMouseNav] = useState(false);
    // isGrabbing drives cursor/user-select + overlay visibility (mirrors m-grabbing)
    const [isGrabbing, setIsGrabbing] = useState(false);

    const heightStyle = { aspectRatio: String(imgAspectRatio) };
    const imageSignature = useMemo(
        () => images.map((image) => image.disBaseLink || image.link || '').join('|'),
        [images]
    );

    // ------------------------------------------------------------------
    // Infinite-loop helpers
    // ------------------------------------------------------------------

    const jumpTo = useCallback((left: number) => {
        const track = trackRef.current;
        if (!track) return;
        isLoopJumping.current = true;
        track.style.scrollBehavior = 'auto';
        track.scrollLeft = left;
        track.style.scrollBehavior = '';
        requestAnimationFrame(() => {
            isLoopJumping.current = false;
        });
    }, []);

    const scrollToIndex = useCallback((targetReal: number) => {
        const track = trackRef.current;
        if (!track || slideWidth.current === 0) return;
        // Real slides start at position 1 (index 0 is the prepended clone)
        track.scrollTo({ left: (targetReal + 1) * slideWidth.current, behavior: 'smooth' });
    }, []);

    const goPrev = useCallback(() => {
        const prev = (currentIndex - 1 + realSlideCount.current) % realSlideCount.current;
        setCurrentIndex(prev);
        scrollToIndex(prev);
    }, [currentIndex, scrollToIndex]);

    const goNext = useCallback(() => {
        const next = (currentIndex + 1) % realSlideCount.current;
        setCurrentIndex(next);
        scrollToIndex(next);
    }, [currentIndex, scrollToIndex]);

    // ------------------------------------------------------------------
    // Clone setup (infinite loop) — re-runs when the rendered image set changes.
    // ------------------------------------------------------------------
    useEffect(() => {
        const track = trackRef.current;
        realSlideCount.current = images.length;
        setCurrentIndex(0);

        if (!track || images.length < 2) {
            setIsInited(true);
            return;
        }

        const items = Array.from(track.children).filter(
            (item): item is HTMLElement => item instanceof HTMLElement && item.dataset.carouselClone !== 'true'
        );

        if (items.length < 2) {
            setIsInited(true);
            return;
        }

        slideWidth.current = items[0].offsetWidth;

        const firstClone = items[0].cloneNode(true) as HTMLElement;
        const lastClone = items[items.length - 1].cloneNode(true) as HTMLElement;
        firstClone.setAttribute('aria-hidden', 'true');
        lastClone.setAttribute('aria-hidden', 'true');
        firstClone.dataset.carouselClone = 'true';
        lastClone.dataset.carouselClone = 'true';

        track.appendChild(firstClone);
        track.insertBefore(lastClone, track.firstChild);

        // Instantly scroll to the real first slide (skip clone at index 0)
        track.style.scrollBehavior = 'auto';
        track.scrollLeft = slideWidth.current;
        track.style.scrollBehavior = '';

        setIsInited(true);

        return () => {
            Array.from(track.children).forEach((item) => {
                if (item instanceof HTMLElement && item.dataset.carouselClone === 'true') {
                    item.remove();
                }
            });
        };
    }, [imageSignature, images.length]);

    // ------------------------------------------------------------------
    // Scroll handler — update pagination index
    // ------------------------------------------------------------------
    const onScroll = useCallback(() => {
        if (isLoopJumping.current) return;
        const track = trackRef.current;
        if (!track || slideWidth.current === 0) return;
        const rawIndex = Math.round(track.scrollLeft / slideWidth.current);
        const real = Math.max(0, Math.min(rawIndex - 1, realSlideCount.current - 1));
        setCurrentIndex(real);
    }, []);

    // ------------------------------------------------------------------
    // ScrollEnd handler — correct infinite-loop position
    // ------------------------------------------------------------------
    const onScrollEnd = useCallback(() => {
        const track = trackRef.current;
        if (!track || isLoopJumping.current || realSlideCount.current < 2) return;

        const firstRealLeft = slideWidth.current;
        const appendedCloneLeft = (realSlideCount.current + 1) * slideWidth.current;

        if (track.scrollLeft < firstRealLeft * 0.5) {
            // Landed on prepended clone → jump to real last
            jumpTo(realSlideCount.current * slideWidth.current);
            setCurrentIndex(realSlideCount.current - 1);
        } else if (track.scrollLeft > appendedCloneLeft - slideWidth.current * 0.5) {
            // Landed on appended clone → jump to real first
            jumpTo(firstRealLeft);
            setCurrentIndex(0);
        }
    }, [jumpTo]);

    // ------------------------------------------------------------------
    // Drag-to-scroll handlers.
    // Attached to the outer wrapper (not the track) so dragging outside the
    // scrollable area doesn't abort the gesture mid-slide. Snap-disable and
    // grabbing classes are removed only after a 200ms delay on release,
    // otherwise the browser snaps the slide back ("bouncing").
    // ------------------------------------------------------------------
    const onMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        const track = trackRef.current;
        if (!track) return;
        // Prevent native text/image selection drag (matches data-event-mousedown.prevent)
        e.preventDefault();
        isDragging.current = false;
        dragActive.current = true;
        dragStartCoord.current = e.clientX;
        dragStartScrollLeft.current = track.scrollLeft;
        setIsMouseNav(true);
        if (grabCleanupTimer.current) {
            clearTimeout(grabCleanupTimer.current);
            grabCleanupTimer.current = null;
        }
    }, []);

    const onMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        const track = trackRef.current;
        if (!dragActive.current || !track) return;
        const delta = dragStartCoord.current - e.clientX;
        if (!delta) return;
        isDragging.current = true;
        setIsGrabbing(true);
        track.scrollLeft = dragStartScrollLeft.current + delta;
    }, []);

    const endDrag = useCallback(() => {
        if (!dragActive.current) return;
        dragActive.current = false;
        // Delay clearing isDragging so the link click handler can read it first
        setTimeout(() => {
            isDragging.current = false;
        }, 0);
        // Remove snap-disable / grabbing with a delay, otherwise it causes bouncing
        grabCleanupTimer.current = setTimeout(() => {
            setIsMouseNav(false);
            setIsGrabbing(false);
            grabCleanupTimer.current = null;
        }, 200);
    }, []);

    const onMouseUp = useCallback(() => {
        endDrag();
    }, [endDrag]);

    const onMouseLeave = useCallback(() => {
        endDrag();
    }, [endDrag]);

    // Suppress link click when user was dragging
    const onLinkClick = useCallback(
        (e: React.MouseEvent) => {
            if (isDragging.current) {
                e.preventDefault();
                e.stopPropagation();
                return;
            }
            handleProductClick?.();
        },
        [handleProductClick]
    );

    // Clear the pending grabbing-cleanup timer on unmount to avoid setting state after unmount
    useEffect(() => {
        return () => {
            if (grabCleanupTimer.current) {
                clearTimeout(grabCleanupTimer.current);
            }
        };
    }, []);

    return (
        <div
            className={cn(
                'group/tile-carousel relative overflow-hidden bg-secondary/20 [&_img]:w-full',
                isInited && 'is-inited',
                isGrabbing && 'cursor-grab select-none',
                className
            )}
            style={heightStyle}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseLeave}>
            {/* Scroll-snap track — snap/smooth-scroll toggled via state classes during drag */}
            <div
                ref={trackRef}
                className={cn(
                    'flex overflow-x-auto overflow-y-hidden',
                    '[scroll-snap-type:x_mandatory] [scroll-behavior:smooth]',
                    // Disable snap during drag, otherwise it fights the manual scrollLeft (m-mousemove_navigation)
                    isMouseNav && '[scroll-snap-type:unset]',
                    // Use instant scroll while grabbing so the track tracks the cursor 1:1 (m-grabbing)
                    isGrabbing && '[scroll-behavior:auto]',
                    // Hide scrollbar cross-browser
                    '[&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]',
                    'w-full h-full'
                )}
                onScroll={onScroll}
                {...{ onScrollEnd }}>
                {images.map((image, i) => {
                    const src = context
                        ? resolveImageUrl({ image, config, context })
                        : image.disBaseLink || image.link || '';
                    return (
                        <Link
                            key={`${src}-${i}`}
                            to={productUrl}
                            onClick={onLinkClick}
                            aria-label={
                                i === 0 ? tProduct('viewProductAriaLabel', { productName }) || productName : undefined
                            }
                            tabIndex={i === 0 ? 0 : -1}
                            className="block flex-shrink-0 w-full [scroll-snap-align:start]">
                            <ProductImage
                                src={src}
                                alt={image.alt || productName}
                                className="w-full h-full"
                                widths={widths}
                                loading={i === 0 ? 'eager' : 'lazy'}
                                imageProps={{ className: 'w-full object-cover' }}
                            />
                        </Link>
                    );
                })}
            </div>

            {/* Grabbing overlay — covers slides during drag so native image-drag and
                link clicks can't fire (mirrors .m-grabbing::before). z-[2] sits above
                slides and below arrows/pagination (z-[3]). */}
            {isGrabbing && <div aria-hidden="true" className="absolute inset-0 z-[2]" />}

            {/* Prev / Next arrows — visible on hover when inited */}
            {isInited && images.length > 1 && (
                <>
                    <button
                        type="button"
                        tabIndex={-1}
                        aria-hidden="true"
                        onClick={goPrev}
                        className={cn(
                            'absolute top-1/2 -translate-y-1/2 left-0 z-[3]',
                            'p-1.5 bg-transparent hover:bg-background transition-all',
                            'opacity-0 group-hover/tile-carousel:opacity-100',
                            'focus-visible:outline-none rounded-none cursor-pointer'
                        )}>
                        <svg
                            aria-hidden="true"
                            width="16"
                            height="28"
                            viewBox="0 0 16 28"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round">
                            <polyline points="12,4 4,14 12,24" />
                        </svg>
                        <span className="sr-only">{t('previousImage')}</span>
                    </button>

                    <button
                        type="button"
                        tabIndex={-1}
                        aria-hidden="true"
                        onClick={goNext}
                        className={cn(
                            'absolute top-1/2 -translate-y-1/2 right-0 z-[3]',
                            'p-1.5 bg-transparent hover:bg-background transition-all',
                            'opacity-0 group-hover/tile-carousel:opacity-100',
                            'focus-visible:outline-none rounded-none cursor-pointer'
                        )}>
                        <svg
                            aria-hidden="true"
                            width="16"
                            height="28"
                            viewBox="0 0 16 28"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round">
                            <polyline points="4,4 12,14 4,24" />
                        </svg>
                        <span className="sr-only">{t('nextImage')}</span>
                    </button>
                </>
            )}

            {/* Segment pagination bar — visible on hover when inited */}
            {isInited && images.length > 1 && (
                <SegmentPagination
                    count={images.length}
                    activeIndex={currentIndex}
                    className="absolute bottom-0 left-0 right-0 opacity-0 group-hover/tile-carousel:opacity-100 transition-opacity z-[3]"
                />
            )}
        </div>
    );
}
